/**
 * Core-100 R5 (CORE-J D) — the person-controlled Connect projection of their work ledger.
 *
 * CORE-J Outputs: "Projected evidence, person-controlled -> CORE-R Connect". The server read
 * `connect_work_ledger` (migration 20270206030600) is the authority: it projects ONLY when the person opted in
 * (default OFF) AND the viewer is in the strict Connect audience (OD-15). This component never decides either.
 *
 *   - The person (self) sees the switch and a preview of exactly what would be projected.
 *   - A Connect viewer sees the projected entries, each with its provenance in words — "Recorded Measurement" /
 *     "Reported by External Source (imported history)" (OD-35) and "Book N min · measured N min" — the same words the Passport ledger uses (CORE-J Prov).
 *     When nothing is projected the section renders nothing: "not shared", "not in the audience" and "no ledger"
 *     look the same to a viewer, on purpose.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { workLedgerSourceLabel } from '@/lib/passport/workLedgerProvenance';

export interface ProjectedLedgerEntry {
  job_name: string | null;
  job_category: string | null;
  vehicle_year: number | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  actual_minutes: number | null;
  predicted_minutes: number | null;
  comeback_flag: boolean;
  completed_month: string | null;
  source_kind: 'observed' | 'imported';
  corrected: boolean;
}

export interface ProjectedLedger {
  entries: ProjectedLedgerEntry[];
  total: number;
  audience: 'self_preview' | 'connect';
  projection_enabled: boolean | null;
}

/** The provenance words for one projected entry. Never implies causation; measured vs book only. */
export function describeProjectedEntry(e: ProjectedLedgerEntry): { source: string; figures: string | null } {
  const source = workLedgerSourceLabel(e.source_kind);
  const figures =
    e.actual_minutes !== null && e.predicted_minutes !== null
      ? `Book ${e.predicted_minutes} min · measured ${e.actual_minutes} min`
      : e.actual_minutes !== null
        ? `Measured ${e.actual_minutes} min (no book time recorded)`
        : null;
  return { source, figures };
}

export function ConnectWorkLedgerProjection({ userId, isSelf }: { userId: string; isSelf: boolean }) {
  const queryClient = useQueryClient();
  const queryKey = ['connect-work-ledger', userId];
  const { data, isError } = useQuery({
    queryKey,
    enabled: !!userId,
    queryFn: async () => {
      const { data: res, error } = await supabase.rpc('connect_work_ledger' as never, { p_user_id: userId } as never);
      if (error) throw error;
      return (res as unknown) as ProjectedLedger;
    },
  });
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase.rpc('set_connect_work_ledger_projection' as never, { p_enabled: enabled } as never);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  if (isError || !data) return null;
  if (!isSelf && (data.total ?? 0) === 0) return null;
  const enabled = data.projection_enabled === true;

  return (
    <Card data-testid="connect-work-ledger">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Work history shared on Connect</CardTitle>
        {isSelf && (
          <div className="flex items-center justify-between gap-3 pt-2">
            <p className="text-sm text-muted-foreground">
              {enabled
                ? 'Recruiting shops on Connect can see the entries below. No shop names, customers or exact dates are shared.'
                : 'Off. Your work history is not shared on Connect. Turn this on to let recruiting shops see the entries below.'}
            </p>
            <Switch
              aria-label="Share my work history on Connect"
              data-testid="connect-work-ledger-switch"
              checked={enabled}
              disabled={toggle.isPending}
              onCheckedChange={(v) => toggle.mutate(v)}
            />
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {data.entries.length === 0 && (
          <p className="text-sm text-muted-foreground">No completed work is recorded yet.</p>
        )}
        {data.entries.map((e, i) => {
          const words = describeProjectedEntry(e);
          return (
            <div key={i} className="rounded-lg border p-3 text-sm" data-testid="connect-work-ledger-entry" data-source={e.source_kind}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{e.job_name ?? 'Job'}</span>
                <span className="text-xs text-muted-foreground">{e.completed_month ?? ''}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {[e.vehicle_year, e.vehicle_make, e.vehicle_model].filter(Boolean).join(' ')}
                {e.job_category ? ` · ${e.job_category}` : ''}
              </p>
              <p className="text-xs">
                <span data-testid="connect-work-ledger-source">{words.source}</span>
                {words.figures && <span> · {words.figures}</span>}
                {e.corrected && <span> · corrected entry</span>}
                {e.comeback_flag && <span> · Comeback</span>}
              </p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
